/**
 * CreationProvenanceBadge
 *
 * Renders the two orthogonal authorship axes:
 * - Source: AI-generated / Automated (human is silent)
 * - Review: Human-reviewed (only when reviewStatus = human_reviewed)
 *
 * Variants:
 * - badge — bordered pill (detail headers)
 * - label — text label (Voice cards / prose)
 * - icon  — icon only, no pill chrome (item list column)
 */

import { Sparkles, CheckCircle2, Cog, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type {
  CreationProvenance,
  CreationSource,
  CreationReviewStatus,
} from '../../../types/prioritization/creationProvenance';
import {
  CREATION_SOURCE_LABELS,
  CREATION_SOURCE_DESCRIPTIONS,
  CREATION_REVIEW_LABELS,
  CREATION_REVIEW_DESCRIPTIONS,
  resolveAuthorship,
} from '../../../types/prioritization/creationProvenance';

export type CreationProvenancePresentation = 'badge' | 'label' | 'icon';

export interface CreationProvenanceBadgeProps {
  source?: CreationSource;
  reviewStatus?: CreationReviewStatus;
  /** @deprecated Prefer source + reviewStatus. */
  provenance?: CreationProvenance;
  compact?: boolean;
  variant?: CreationProvenancePresentation;
  className?: string;
}

function AuthorshipMark({
  icon: Icon,
  label,
  tooltip,
  classes,
  textClasses,
  compact,
  variant,
  className,
}: {
  icon: LucideIcon;
  label: string;
  tooltip: string;
  classes: string;
  textClasses: string;
  compact: boolean;
  variant: CreationProvenancePresentation;
  className: string;
}) {
  if (variant === 'icon') {
    return (
      <span
        className={`inline-flex items-center justify-center ${textClasses} ${className}`}
        title={tooltip}
        aria-label={`${label} — ${tooltip}`}
      >
        <Icon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} strokeWidth={2} />
      </span>
    );
  }

  if (variant === 'label') {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-medium tracking-wide ${
          compact ? '' : 'uppercase'
        } ${textClasses} ${className}`}
        title={tooltip}
        aria-label={compact ? `${label} — ${tooltip}` : undefined}
      >
        <Icon className="h-3 w-3 shrink-0" />
        {!compact && label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border text-xs font-medium ${
        compact ? 'p-1' : 'px-2 py-0.5'
      } ${classes} ${className}`}
      title={tooltip}
      aria-label={compact ? `${label} — ${tooltip}` : undefined}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {!compact && label}
    </span>
  );
}

const SOURCE_VISUAL: Record<
  Exclude<CreationSource, 'human'>,
  { icon: LucideIcon; classes: string; textClasses: string }
> = {
  ai: {
    icon: Sparkles,
    classes:
      'bg-transparent border-[#D4A017] text-[#D4A017] dark:border-[#D4A017] dark:text-[#e0b125]',
    textClasses: 'text-[#D4A017] dark:text-[#e0b125]',
  },
  automated: {
    icon: Cog,
    classes:
      'bg-transparent border-[var(--rule)] text-[var(--text-sec)]',
    textClasses: 'text-[var(--text-sec)]',
  },
};

export function CreationProvenanceBadge({
  source,
  reviewStatus,
  provenance,
  compact = false,
  variant = 'badge',
  className = '',
}: CreationProvenanceBadgeProps) {
  const authorship = resolveAuthorship({ source, reviewStatus, provenance });

  if (authorship.source === 'human' && authorship.reviewStatus === 'none') {
    return null;
  }

  const marks: ReactNode[] = [];

  if (authorship.source !== 'human') {
    const visual = SOURCE_VISUAL[authorship.source];
    marks.push(
      <AuthorshipMark
        key="source"
        icon={visual.icon}
        label={CREATION_SOURCE_LABELS[authorship.source]}
        tooltip={CREATION_SOURCE_DESCRIPTIONS[authorship.source]}
        classes={visual.classes}
        textClasses={visual.textClasses}
        compact={compact}
        variant={variant}
        className=""
      />,
    );
  }

  if (authorship.reviewStatus === 'human_reviewed') {
    marks.push(
      <AuthorshipMark
        key="review"
        icon={CheckCircle2}
        label={CREATION_REVIEW_LABELS.human_reviewed}
        tooltip={CREATION_REVIEW_DESCRIPTIONS.human_reviewed}
        classes="bg-[#3A7D56] border-[#3A7D56] text-white dark:bg-[#3A7D56] dark:border-[#3A7D56]"
        textClasses="text-[#3A7D56] dark:text-[#5a9d76]"
        compact={compact}
        variant={variant}
        className=""
      />,
    );
  }

  if (marks.length === 0) return null;

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>{marks}</span>
  );
}
